/**
 * Google Ads API Currency Conversion Utilities
 *
 * The Google Ads API represents currency values in "micros" where 1 dollar = 1,000,000 micros.
 */

/**
 * Conversion factor: 1 dollar = 1,000,000 micros
 */
export const MICROS_PER_DOLLAR = 1_000_000;

/**
 * Convert dollars to Google Ads API micros
 *
 * @param dollars - Amount in dollars
 * @returns Amount in micros as an integer (1,000,000 micros = $1.00)
 *
 * @example
 * ```typescript
 * dollarsToMicros(5.5)   // Returns 5500000
 * dollarsToMicros(0.01)  // Returns 10000
 * ```
 */
export function dollarsToMicros(dollars: number | string): number {
  const dollarAmount = typeof dollars === 'string' ? parseFloat(dollars) : dollars;
  return Math.round(dollarAmount * MICROS_PER_DOLLAR);
}

/**
 * Convert Google Ads API micros to dollars
 *
 * @param micros - Amount in micros (1,000,000 micros = $1.00). Can be null/undefined.
 * @returns Amount in dollars. Returns 0 if micros is null/undefined.
 *
 * @example
 * ```typescript
 * microsToDollars(5500000)  // Returns 5.5
 * microsToDollars(null)     // Returns 0
 * ```
 */
export function microsToDollars(micros: number | null | undefined): number {
  if (micros === null || micros === undefined) {
    return 0;
  }
  return micros / MICROS_PER_DOLLAR;
}

/**
 * Format Google Ads account ID with dashes for display
 *
 * @param accountId - Google Ads account ID (10 digits). Can be null/undefined.
 * @returns Formatted account ID (e.g., "123-456-7890"). Returns "Unknown" if null/undefined.
 *
 * @example
 * ```typescript
 * formatAccountId("1234567890")  // Returns "123-456-7890"
 * formatAccountId(null)          // Returns "Unknown"
 * formatAccountId("123")         // Returns "123" (unchanged if not 10 digits)
 * ```
 */
export function formatAccountId(accountId: string | undefined | null): string {
  if (!accountId) return 'Unknown';
  const cleaned = accountId.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return accountId;
}
